/**
 * @file useDeleteReauth.ts
 * @brief State for the extra proof a delete asks for (B33): which proof
 *        (code or sign in again), the typed code, and the line under it.
 *        The screens are in `DeleteReauth.tsx`.
 *
 * @author DES (prepared with Claude)
 * @date 26-10-2026
 */

import { useRef, useState } from "react";
import { describeTwoFactorError, errorBody } from "@/features/auth/twoFactor/twoFactorApi";
import { isReauthCodeError, reauthKind, reauthProof } from "@/features/auth/twoFactor/twoFactorRules";
import { ApiClientError } from "@/shared/api/client";
import { useAuth } from "@/shared/auth/auth-context";

export type DeleteProof = { twoFactorCode: string } | { backupCode: string };

export function useDeleteReauth() {
  const { cancelTwoFactor } = useAuth();
  const [mode, setMode] = useState<"code" | "signIn" | null>(null);
  const [code, setCode] = useState("");
  const [backup, setBackup] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const refocus = () => window.setTimeout(() => inputRef.current?.focus(), 0);

  /**
   * The code fields for the next send: `{}` outside code mode, null (with
   * the line under the box set) when what is typed is not a code.
   */
  const proof = (): DeleteProof | Record<string, never> | null => {
    if (mode !== "code") return {};
    const p = reauthProof(code, backup);
    if (!p) {
      setError(backup ? "Enter one of your backup codes." : "Enter the 6-digit code from your authenticator app.");
      refocus();
    }
    return p;
  };

  /**
   * A failed delete: true when it is shown here (re-auth asked, or a code
   * error under the box), false for the caller's toast. `afterSignIn`: the
   * fresh session was refused too, so it is not asked again.
   */
  const handleError = (err: unknown, afterSignIn: boolean): boolean => {
    if (!(err instanceof ApiClientError)) return false;
    const kind = reauthKind(err.code, errorBody(err));
    if (kind) {
      if (afterSignIn) return false;
      setMode(kind);
      setCode("");
      setError(null);
      if (kind === "code") refocus();
      return true;
    }
    if (mode === "code" && isReauthCodeError(err.code)) {
      setError(describeTwoFactorError(err));
      setCode("");
      refocus();
      return true;
    }
    return false;
  };

  const reset = () => {
    setMode(null);
    setCode("");
    setBackup(false);
    setError(null);
    cancelTwoFactor();
  };

  return { mode, code, setCode, backup, setBackup, error, setError, inputRef, proof, handleError, reset };
}

export type DeleteReauth = ReturnType<typeof useDeleteReauth>;
